import { CatHeading } from '@blocks-react/components/Heading';
import { CatIconEmail } from '@blocks-react/components/Icons/Email';
import { CatIconHyperlink } from '@blocks-react/components/Icons/Hyperlink';
import { CatIconLocation } from '@blocks-react/components/Icons/Location';
import { CatIconPhone } from '@blocks-react/components/Icons/Phone';
import { Conditional } from '../conditional';
import { LinkWithIcon } from '../link-with-icon';
import { StoreContactInfoProps } from './types';
import { getDrivingDirections, getWebsiteUri } from './utils';
import { useMemo } from 'react';

export const StoreContactInfo = ({
  title,
  phone,
  phoneNumberLabel,
  email,
  emailLabel,
  emailTarget = '_blank',
  contactUrl = '',
  website = '',
  websiteLabel,
  latitude = '',
  longitude = '',
  drivingDirectionsLabel,
  linkClassName,
  linkTextClassName,
  isStoreLocationOptimizationFlag = false,
  forStoreInfoDrawer = false
}: StoreContactInfoProps) => {
  const drivingDirections = getDrivingDirections(latitude, longitude);
  const showEmail = !!email && !!contactUrl;
  const showDrivingDirections = !!latitude && !!longitude;
  const showStoreContactInfo =
    !!phone || !!website || showEmail || showDrivingDirections;
  const showPhone = useMemo(
    () => (
      <Conditional test={!!phone}>
        <LinkWithIcon
          linkClassName={linkClassName}
          linkTextClassName={linkTextClassName}
          href={`tel:${phone}`}
          icon={<CatIconPhone />}
          aria-label={`${phoneNumberLabel}${phone}`}
        >
          {phone}
        </LinkWithIcon>
      </Conditional>
    ),
    [phone]
  );
  const showEmailUi = useMemo(
    () => (
      <Conditional test={showEmail}>
        <LinkWithIcon
          linkClassName={linkClassName}
          linkTextClassName={linkTextClassName}
          href={contactUrl}
          target={emailTarget}
          icon={<CatIconEmail />}
          aria-label={`${emailLabel}${contactUrl}`}
        >
          {emailLabel}
        </LinkWithIcon>
      </Conditional>
    ),
    [showEmail]
  );
  const websiteUI = useMemo(
    () => (
      <Conditional test={!!website}>
        <LinkWithIcon
          linkClassName={linkClassName}
          linkTextClassName={linkTextClassName}
          href={getWebsiteUri(website)}
          target="_blank"
          icon={<CatIconHyperlink />}
          aria-label={`${websiteLabel}${website}`}
        >
          {websiteLabel}
        </LinkWithIcon>
      </Conditional>
    ),
    [website]
  );
  const showDrivingDirectionsUI = useMemo(
    () => (
      <Conditional test={showDrivingDirections}>
        <LinkWithIcon
          linkClassName={linkClassName}
          linkTextClassName={`${linkTextClassName} text-lowercase`}
          href={drivingDirections}
          target="_blank"
          icon={<CatIconLocation />}
          aria-label={`${drivingDirectionsLabel}${drivingDirections}`}
        >
          {drivingDirectionsLabel}
        </LinkWithIcon>
      </Conditional>
    ),
    [showDrivingDirections]
  );
  return (
    <Conditional test={showStoreContactInfo}>
      <CatHeading
        level="h2"
        variant={'title-sm'}
        className={isStoreLocationOptimizationFlag ? 'mt-4' : ''}
      >
        {title}
      </CatHeading>
      <Conditional
        test={isStoreLocationOptimizationFlag}
        fallback={
          <>
            {showPhone}
            {showEmailUi}
            {websiteUI}
            {showDrivingDirectionsUI}
          </>
        }
      >
        <div className="container p-0">
          <div className="row ">
            <div className="col-6">
              <div className="mb-3">{showDrivingDirectionsUI}</div>
              {showEmailUi}
            </div>
            <div className="col-6">
              <div className="mb-3">{showPhone}</div>
              {websiteUI}
            </div>
          </div>
        </div>
      </Conditional>
    </Conditional>
  );
};
